/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import Navbar from '@/components/layout/Navbar';
import {
  ArrowLeft,
  Upload,
  Sparkles,
  Loader2,
  CalendarIcon,
  MapPin,
  Instagram,
  Globe,
  Ticket,
  PoundSterling,
  Link as LinkIcon,
  CheckCircle,
  Wand2,
  RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

/**
 * AI Flyer Auto-Extract — independent path that pre-fills the standard
 * event-create payload from a flyer image. The final POST /api/events
 * call is byte-identical to the manual flow's submission shape.
 */
const CreateEventAutoPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // 'idle' → user picks image
  // 'extracting' → showing spinner
  // 'preview' → editable form pre-filled
  const [stage, setStage] = useState('idle');
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [llmNotes, setLlmNotes] = useState(null);

  // Pre-filled form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    date: null,                 // Date object
    time: '19:00',
    end_time: '23:00',
    address: '',
    lat: 51.5074,
    lng: -0.1278,
    instagram_url: '',
    website: '',
    age_restriction: '',
    dress_code: '',
    has_bar: false,
    has_food_vendors: false,
    has_onsite_parking: false,
    has_toilets: false,
    has_skate_hire: false,
    nearest_stations: [],
    flyer_url: '',
  });

  // Optional manual-only post-extraction fields
  const [ticketLink, setTicketLink] = useState('');     // external URL only
  const [sellTickets, setSellTickets] = useState(false);
  const [ticketPrice, setTicketPrice] = useState('');    // £
  const [ticketQuantity, setTicketQuantity] = useState('');

  // Geocode the address → lat/lng once user finishes editing it
  const [geocoding, setGeocoding] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      toast.error('Please pick a JPEG, PNG, WebP or GIF flyer');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Flyer must be under 15MB');
      return;
    }

    setExtracting(true);
    setStage('extracting');
    setLlmNotes(null);
    try {
      const dataUrl = await fileToBase64(file);
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/events/extract-flyer`,
        { file_data: dataUrl, filename: file.name || 'flyer.jpg' },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );

      const ex = data.extracted || {};

      // Date string → Date object (best effort)
      let dateObj = null;
      if (ex.date) {
        const d = new Date(ex.date);
        if (!Number.isNaN(d.getTime())) dateObj = d;
      }

      setForm((prev) => ({
        ...prev,
        name: ex.name || '',
        description: ex.description || '',
        date: dateObj,
        time: ex.time || prev.time,
        end_time: ex.end_time || prev.end_time,
        address: ex.address || '',
        instagram_url: ex.instagram_url || '',
        website: ex.website || '',
        age_restriction: ex.age_restriction || '',
        dress_code: ex.dress_code || '',
        has_bar: !!ex.has_bar,
        has_food_vendors: !!ex.has_food_vendors,
        has_onsite_parking: !!ex.has_onsite_parking,
        has_toilets: !!ex.has_toilets,
        has_skate_hire: !!ex.has_skate_hire,
        nearest_stations: Array.isArray(ex.nearest_stations) ? ex.nearest_stations : [],
        flyer_url: data.flyer_url || '',
      }));
      setLlmNotes(data.notes || null);
      setStage('preview');

      // Best-effort geocode in the background
      if (ex.address) geocode(ex.address);

      toast.success('Flyer read! Review the details below.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not read the flyer. Try a clearer image.';
      toast.error(msg);
      setStage('idle');
    } finally {
      setExtracting(false);
    }
  };

  const geocode = async (addr) => {
    if (!addr) return;
    setGeocoding(true);
    try {
      // Re-use the existing free Nominatim proxy on the backend
      const { data } = await axios.get(
        `${API}/world-events/geocode`,
        { params: { q: addr } },
      );
      if (data?.lat && data?.lng) {
        setForm((p) => ({ ...p, lat: data.lat, lng: data.lng }));
      }
    } catch {
      /* silent — user can still submit; backend doesn't reject on coarse coords */
    } finally {
      setGeocoding(false);
    }
  };

  const handleSubmit = async () => {
    // Light validation — flyer/extract guarantees a clean image, but the user
    // must have filled in the basics before we hit /api/events.
    if (!form.name.trim()) return toast.error('Event name is required');
    if (!form.date) return toast.error('Pick a date');
    if (!form.address.trim()) return toast.error('Venue address is required');
    if (!form.flyer_url) return toast.error('Flyer image is missing');

    if (sellTickets) {
      const p = parseFloat(ticketPrice);
      if (Number.isNaN(p) || p < 0) return toast.error('Enter a valid ticket price');
      const q = parseInt(ticketQuantity, 10);
      if (Number.isNaN(q) || q <= 0) return toast.error('Enter how many tickets to sell');
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');

      // Compose the start ISO timestamp from date+time
      const [h, m] = (form.time || '19:00').split(':');
      const eventDate = new Date(form.date);
      eventDate.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);

      let endIso = null;
      if (form.end_time) {
        const [eh, em] = form.end_time.split(':');
        const endDate = new Date(form.date);
        endDate.setHours(parseInt(eh, 10), parseInt(em, 10), 0, 0);
        endIso = endDate.toISOString();
      }

      const payload = {
        name: form.name,
        description: form.description,
        date: eventDate.toISOString(),
        end_time: endIso,
        venue_type: 'event',
        category: 'skate',
        sport: 'roller_skate',
        location: {
          address: form.address,
          lat: form.lat,
          lng: form.lng,
        },
        // Use the same flyer as both the thumbnail (image_url) and full
        // flyer — backend's _or_ fallback handles this identically.
        image_url: form.flyer_url,
        flyer_url: form.flyer_url,
        ticket_link: sellTickets ? null : (ticketLink || null),
        instagram_url: form.instagram_url || null,
        has_onsite_parking: form.has_onsite_parking,
        alternative_parking: null,
        requires_id: false,
        has_bar: form.has_bar,
        has_food_vendors: form.has_food_vendors,
        has_toilets: form.has_toilets,
        has_skate_hire: form.has_skate_hire,
        nearest_stations: (form.nearest_stations || []).filter((s) => s.trim()),
        thumbnail_position: { x: 50, y: 50, scale: 1 },
        tickets_enabled: !!sellTickets,
        ticket_price_pence: sellTickets ? Math.round(parseFloat(ticketPrice) * 100) : null,
        ticket_quantity: sellTickets ? parseInt(ticketQuantity, 10) : null,
        max_per_buyer: 6,
        cash_payment_enabled: false,
        is_recurring: false,
        recurrence_pattern: null,
        additional_dates: [],
        age_restriction: form.age_restriction || null,
        dress_code: form.dress_code || null,
        event_website: form.website || null,
      };

      const res = await axios.post(`${API}/events`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Event created!');
      navigate(`/events/${res.data.event_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create event');
    } finally {
      setSubmitting(false);
    }
  };

  // ── render helpers ──
  const renderIdle = () => (
    <Card data-testid="auto-flyer-upload-card" className="bg-card border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Wand2 className="w-5 h-5 text-accent" />
          Auto-fill from Flyer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Drop your event flyer and we'll read the name, date, venue, vibe — everything you'd normally type in.
          Review, tweak, hit publish. ~3 seconds.
        </p>
        <div
          data-testid="auto-flyer-dropzone"
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-border/60 hover:border-accent transition rounded-lg p-10 text-center cursor-pointer"
        >
          <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Tap to upload flyer</p>
          <p className="text-xs text-muted-foreground mt-1">JPEG / PNG / WebP · up to 15MB</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="auto-flyer-input"
        />
      </CardContent>
    </Card>
  );

  const renderExtracting = () => (
    <Card className="bg-card border-border/50">
      <CardContent className="p-12 text-center space-y-4">
        <Loader2 className="w-12 h-12 mx-auto animate-spin text-accent" />
        <p className="font-medium">Reading your flyer…</p>
        <p className="text-sm text-muted-foreground">
          Spotting the date, venue, vibe — usually under 5 seconds.
        </p>
      </CardContent>
    </Card>
  );

  const renderPreview = () => (
    <div className="space-y-4">
      {/* Flyer preview */}
      {form.flyer_url && (
        <Card className="bg-card border-border/50 overflow-hidden">
          <img
            src={form.flyer_url}
            alt="Flyer"
            className="w-full max-h-[420px] object-contain bg-black/40"
            data-testid="auto-flyer-preview-image"
          />
          <div className="p-3 flex items-center justify-between">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
              Flyer uploaded
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              data-testid="auto-flyer-replace-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Replace
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
        </Card>
      )}

      {llmNotes && (
        <Alert>
          <AlertDescription className="text-sm">
            <Sparkles className="w-3.5 h-3.5 inline mr-1 text-accent" />
            {llmNotes}
          </AlertDescription>
        </Alert>
      )}

      {/* Core details */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-base">Event details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="auto-name">Event name</Label>
            <Input
              id="auto-name"
              data-testid="auto-name-input"
              maxLength={60}
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>

          <div>
            <Label htmlFor="auto-desc">Description</Label>
            <Textarea
              id="auto-desc"
              data-testid="auto-description-input"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn('w-full justify-start text-left', !form.date && 'text-muted-foreground')}
                    data-testid="auto-date-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {form.date ? format(form.date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.date || undefined}
                    onSelect={(d) => setForm((p) => ({ ...p, date: d || null }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Start</Label>
                <Input
                  data-testid="auto-time-input"
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm((p) => ({ ...p, time: e.target.value }))}
                />
              </div>
              <div>
                <Label>End</Label>
                <Input
                  data-testid="auto-end-time-input"
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm((p) => ({ ...p, end_time: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="auto-address">
              <MapPin className="w-3.5 h-3.5 inline mr-1" />
              Venue address
            </Label>
            <Input
              id="auto-address"
              data-testid="auto-address-input"
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              onBlur={() => geocode(form.address)}
              placeholder="The Roller Room, 12 High Street, London SW9 8AB"
            />
            {geocoding && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Locating on map…
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label><Instagram className="w-3.5 h-3.5 inline mr-1" />Instagram</Label>
              <Input
                data-testid="auto-instagram-input"
                value={form.instagram_url}
                onChange={(e) => setForm((p) => ({ ...p, instagram_url: e.target.value }))}
                placeholder="https://instagram.com/your_handle"
              />
            </div>
            <div>
              <Label><Globe className="w-3.5 h-3.5 inline mr-1" />Website</Label>
              <Input
                data-testid="auto-website-input"
                value={form.website}
                onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
                placeholder="https://…"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Age restriction</Label>
              <Input
                data-testid="auto-age-input"
                value={form.age_restriction}
                onChange={(e) => setForm((p) => ({ ...p, age_restriction: e.target.value }))}
                placeholder="18+"
              />
            </div>
            <div>
              <Label>Dress code</Label>
              <Input
                data-testid="auto-dress-input"
                value={form.dress_code}
                onChange={(e) => setForm((p) => ({ ...p, dress_code: e.target.value }))}
                placeholder="Disco glam"
              />
            </div>
          </div>

          <div>
            <Label>Nearest stations (comma-separated)</Label>
            <Input
              data-testid="auto-stations-input"
              value={(form.nearest_stations || []).join(', ')}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  nearest_stations: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                }))
              }
              placeholder="Brixton, Stockwell"
            />
          </div>

          {/* Amenity toggles */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            {[
              ['has_bar', 'Bar'],
              ['has_food_vendors', 'Food vendors'],
              ['has_onsite_parking', 'On-site parking'],
              ['has_toilets', 'Toilets'],
              ['has_skate_hire', 'Skate hire'],
            ].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between rounded-md border border-border/40 px-3 py-2">
                <span className="text-sm">{label}</span>
                <Switch
                  data-testid={`auto-toggle-${k}`}
                  checked={form[k]}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, [k]: !!v }))}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Optional pricing / tickets / external link */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Ticket className="w-4 h-4 text-accent" />
            Tickets &amp; links
            <span className="text-xs font-normal text-muted-foreground">(optional)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-border/40 px-3 py-2">
            <div>
              <p className="text-sm font-medium">Sell tickets through RollM8s</p>
              <p className="text-xs text-muted-foreground">
                We handle Stripe checkout and email tickets. Leave off if this flyer is just for advertising.
              </p>
            </div>
            <Switch
              data-testid="auto-sell-tickets-switch"
              checked={sellTickets}
              onCheckedChange={(v) => setSellTickets(!!v)}
            />
          </div>

          {sellTickets ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label><PoundSterling className="w-3.5 h-3.5 inline mr-1" />Ticket price (£)</Label>
                <Input
                  data-testid="auto-ticket-price-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={ticketPrice}
                  onChange={(e) => setTicketPrice(e.target.value)}
                  placeholder="10.00"
                />
              </div>
              <div>
                <Label>How many tickets?</Label>
                <Input
                  data-testid="auto-ticket-quantity-input"
                  type="number"
                  min="1"
                  value={ticketQuantity}
                  onChange={(e) => setTicketQuantity(e.target.value)}
                  placeholder="100"
                />
              </div>
            </div>
          ) : (
            <div>
              <Label><LinkIcon className="w-3.5 h-3.5 inline mr-1" />External ticket link</Label>
              <Input
                data-testid="auto-ticket-link-input"
                value={ticketLink}
                onChange={(e) => setTicketLink(e.target.value)}
                placeholder="https://eventbrite.com/…"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave blank if this flyer is purely promotional.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Button
        data-testid="auto-publish-btn"
        className="w-full"
        size="lg"
        disabled={submitting}
        onClick={handleSubmit}
      >
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        Publish event
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="create-event-auto-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/create-event')}
            data-testid="auto-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Manual form
          </Button>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-accent" />
            Auto-fill from Flyer
          </h1>
          <span className="w-[88px]" />
        </div>

        {stage === 'idle' && renderIdle()}
        {stage === 'extracting' && renderExtracting()}
        {stage === 'preview' && renderPreview()}
      </div>
    </div>
  );
};

export default CreateEventAutoPage;
